import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTenant } from '../libs/contexts/TenantContext';
import { useEventSystemCache } from '../context/EventSystemCacheContext';
import { actionCacheKey } from '../services/eventSystem/actionCacheScope';
import { flowApi, actionApi, deviceApi } from '../services/eventSystemApiServiceFixed';
import { useAuth } from '../contexts/AuthContext';
import { useDealerAdmin } from '../contexts/DealerAdminContext';

/**
 * Custom hook to interact with the event system API
 * Handles tenant selection and caching
 */
const useEventSystemFixed = () => {
  const { selectedTenant } = useTenant();
  const { getCache, updateCache, clearCache } = useEventSystemCache();
  const { tenantId: tenantIdFromAuth } = useAuth();
  const dealerAdmin = useDealerAdmin();

  const [loading, setLoading] = useState({
    flows: false,
    actions: false,
    devices: false,
  });

  const [error, setError] = useState({
    flows: null,
    actions: null,
    devices: null,
  });

  // Circuit breaker state to prevent rapid API calls
  const [lastApiCalls, setLastApiCalls] = useState({
    flows: null,
    actions: null,
    devices: null,
  });

  // Get tenant ID strictly from context or user claims (no localStorage/defaults)
  const getTenantId = useCallback(() => {
    if (dealerAdmin.tenantId) {
      return dealerAdmin.tenantId;
    }
    if (selectedTenant && selectedTenant.id) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`useEventSystemFixed: Using tenant ID from selectedTenant: ${selectedTenant.id}`);
      }
      return selectedTenant.id;
    }
    if (tenantIdFromAuth) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`useEventSystemFixed: Using tenant ID from auth context: ${tenantIdFromAuth}`);
      }
      return tenantIdFromAuth;
    }
    if (process.env.NODE_ENV !== 'production') {
      console.warn('useEventSystemFixed: No tenantId available (TenantContext/currentUser missing)');
    }
    return null;
  }, [dealerAdmin.tenantId, selectedTenant?.id, tenantIdFromAuth]);

  /**
   * Fetch flows with proper tenant ID
   */
  const fetchFlows = useCallback(
    async (forceRefresh = false) => {
      const tenantId = getTenantId();
      if (process.env.NODE_ENV !== 'production') {
        console.log(`useEventSystemFixed: fetchFlows with tenantId: ${tenantId}`);
      }

      // Check if we have a valid tenant ID
      if (!tenantId) {
        console.error('No tenant ID available for fetching flows');
        setError(prev => ({ ...prev, flows: 'No tenant selected' }));
        return [];
      }

      // Circuit breaker: prevent rapid API calls
      const cacheKey = actionCacheKey('flows', tenantId);
      const now = Date.now();
      if (!forceRefresh && lastApiCalls.flowsKey === cacheKey && lastApiCalls.flows && now - lastApiCalls.flows < 3000) {
        const cachedFlows = getCache(cacheKey);
        return Array.isArray(cachedFlows) ? cachedFlows : [];
      }

      if (!forceRefresh) {
        const cachedFlows = getCache(cacheKey);
        if (cachedFlows) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Using cached flows data');
          }
          return cachedFlows;
        }
      }

      // Fetch from API
      setLoading(prev => ({ ...prev, flows: true }));
      setError(prev => ({ ...prev, flows: null }));
      setLastApiCalls(prev => ({ ...prev, flows: now, flowsKey: cacheKey }));

      try {
        if (process.env.NODE_ENV !== 'production') {
          console.log(`Fetching flows for tenant: ${tenantId}`);
        }
        const response = await flowApi.getAll(tenantId);

        // Handle different response formats
        let flows = [];
        if (response && response.data) {
          flows = Array.isArray(response.data) ? response.data : [];
          if (process.env.NODE_ENV !== 'production') {
            console.log(`Received ${flows.length} flows from API response.data`);
          }
        } else if (Array.isArray(response)) {
          flows = response;
          if (process.env.NODE_ENV !== 'production') {
            console.log(`Received ${flows.length} flows from API array response`);
          }
        } else {
          console.warn('Unexpected API response format for flows:', response);
        }

        // Update cache
        updateCache(cacheKey, flows);
        setLoading(prev => ({ ...prev, flows: false }));
        return flows;
      } catch (err) {
        console.error('Error fetching flows:', err);
        setError(prev => ({ ...prev, flows: err.message || 'Failed to fetch flows' }));
        setLoading(prev => ({ ...prev, flows: false }));
        return [];
      }
    },
    [getTenantId, getCache, updateCache]
  );

  /**
   * Fetch actions with proper tenant ID
   */
  const fetchActions = useCallback(
    async (forceRefresh = false) => {
      const tenantId = getTenantId();
      if (process.env.NODE_ENV !== 'production') {
        console.log(`useEventSystemFixed: fetchActions with tenantId: ${tenantId}`);
      }

      // Check if we have a valid tenant ID
      if (!tenantId) {
        console.error('No tenant ID available for fetching actions');
        setError(prev => ({ ...prev, actions: 'No tenant selected' }));
        return [];
      }

      const cacheKey = actionCacheKey('actions', tenantId);
      const now = Date.now();
      if (!forceRefresh && lastApiCalls.actionsKey === cacheKey && lastApiCalls.actions && now - lastApiCalls.actions < 3000) {
        const cachedActions = getCache(cacheKey);
        return Array.isArray(cachedActions) ? cachedActions : [];
      }

      if (!forceRefresh) {
        const cachedActions = getCache(cacheKey);
        if (cachedActions) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Using cached actions data');
          }
          return cachedActions;
        }
      }

      // Fetch from API
      setLoading(prev => ({ ...prev, actions: true }));
      setError(prev => ({ ...prev, actions: null }));
      setLastApiCalls(prev => ({ ...prev, actions: now, actionsKey: cacheKey }));

      try {
        if (process.env.NODE_ENV !== 'production') {
          console.log(`Fetching actions for tenant: ${tenantId}`);
        }
        const response = await actionApi.getAll(tenantId);

        // Handle different response formats
        let actions = [];
        if (response && response.data) {
          actions = Array.isArray(response.data) ? response.data : [];
          if (process.env.NODE_ENV !== 'production') {
            console.log(`Received ${actions.length} actions from API response.data`);
          }
        } else if (Array.isArray(response)) {
          actions = response;
          if (process.env.NODE_ENV !== 'production') {
            console.log(`Received ${actions.length} actions from API array response`);
          }
        } else {
          console.warn('Unexpected API response format for actions:', response);
        }

        // Normalize actions to UI shape regardless of backend format
        const normalizedActions = (actions || []).map(a => ({
          ...a,
          // Ensure we always have an id field for list keys and updates
          id: a.id ?? a._id ?? a.actionId ?? a.uuid ?? null,
          // Map backend types back to UI types for consistent rendering/UX
          type: (() => {
            const t = (a.type || '').toString().toLowerCase();
            if (t === 'twilio_sms') return 'sms';
            if (t === 'twilio_email') return 'email';
            if (t === 'twilio_sip') return 'sip';
            if (t === 'webhook') return 'webhook';
            if (t === 'device_action') return 'device';
            if (t === 'desktop_action') return 'desktop';
            if (t === 'rest') return 'webhook'; // treat generic REST as webhook in UI
            return a.type || 'other';
          })(),
          // Normalize enabled -> isActive for status pill
          isActive:
            typeof a.isActive === 'boolean'
              ? a.isActive
              : typeof a.enabled === 'boolean'
                ? a.enabled
                : false,
          // Normalize device id field variants
          targetDeviceId: a.targetDeviceId ?? a.target_device_id ?? a.deviceId ?? a.device_id ?? '',
          // Normalize webhook URL field
          webhookUrl: a.webhookUrl ?? a.url ?? '',
        }));

        // Update cache with normalized actions
        updateCache(cacheKey, normalizedActions);
        setLoading(prev => ({ ...prev, actions: false }));
        return normalizedActions;
      } catch (err) {
        console.error('Error fetching actions:', err);
        setError(prev => ({ ...prev, actions: err.message || 'Failed to fetch actions' }));
        setLoading(prev => ({ ...prev, actions: false }));
        return [];
      }
    },
    [getTenantId, getCache, updateCache]
  );

  /**
   * Fetch devices with proper tenant ID
   */
  const fetchDevices = useCallback(
    async (forceRefresh = false) => {
      const tenantId = getTenantId();
      if (process.env.NODE_ENV !== 'production') {
        console.log(`useEventSystemFixed: fetchDevices with tenantId: ${tenantId}`);
      }

      // Check if we have a valid tenant ID
      if (!tenantId) {
        console.error('No tenant ID available for fetching devices');
        setError(prev => ({ ...prev, devices: 'No tenant selected' }));
        return [];
      }

      const cacheKey = actionCacheKey('devices', tenantId);
      const now = Date.now();
      if (!forceRefresh && lastApiCalls.devicesKey === cacheKey && lastApiCalls.devices && now - lastApiCalls.devices < 3000) {
        const cachedDevices = getCache(cacheKey);
        return Array.isArray(cachedDevices) ? cachedDevices : [];
      }

      if (!forceRefresh) {
        const cachedDevices = getCache(cacheKey);
        if (cachedDevices) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Using cached devices data');
          }
          return cachedDevices;
        }
      }

      // Fetch from API
      setLoading(prev => ({ ...prev, devices: true }));
      setError(prev => ({ ...prev, devices: null }));
      setLastApiCalls(prev => ({ ...prev, devices: now, devicesKey: cacheKey }));

      try {
        if (process.env.NODE_ENV !== 'production') {
          console.log(`Fetching devices for tenant: ${tenantId}`);
        }
        const response = await deviceApi.getAll(tenantId);

        // Handle different response formats
        let devices = [];
        if (response && response.data) {
          devices = Array.isArray(response.data) ? response.data : [];
          if (process.env.NODE_ENV !== 'production') {
            console.log(`Received ${devices.length} devices from API response.data`);
          }
        } else if (Array.isArray(response)) {
          devices = response;
          if (process.env.NODE_ENV !== 'production') {
            console.log(`Received ${devices.length} devices from API array response`);
          }
        } else {
          console.warn('Unexpected API response format for devices:', response);
        }

        // Normalize to UI shape regardless of source
        const normalizedDevices = (devices || []).map(d => ({
          ...d,
          // Map backend fields to UI fields
          label: d.label ?? d.name ?? 'Unnamed Device',
          deviceType: d.deviceType ?? d.type ?? 'other',
          isActive: typeof d.isActive === 'boolean' ? d.isActive : true,
          agentId: d.agentId ?? d.agent_id ?? d.agentID ?? '',
          ipAddress: d.ipAddress ?? d.ip ?? d.ip_address ?? '',
          capabilities: Array.isArray(d.capabilities) ? d.capabilities : [],
          targets: d.targets || {},
        }));

        // Deduplicate devices by MAC address (golden key), then by ID as fallback
        const seenMacs = new Set();
        const seenIds = new Set();
        const deduplicatedDevices = normalizedDevices.filter(d => {
          const mac = (d.mac || d.macAddress || d.mac_address || '').toUpperCase().replace(/[^A-F0-9]/g, '');
          const id = d.id || d.deviceId || d._id;
          if (mac && mac.length >= 12) {
            if (seenMacs.has(mac)) return false;
            seenMacs.add(mac);
            if (id) seenIds.add(id);
            return true;
          }
          if (!id || seenIds.has(id)) return false;
          seenIds.add(id);
          return true;
        });

        // Update cache with final devices list (API or Firestore)
        updateCache(cacheKey, deduplicatedDevices);
        setLoading(prev => ({ ...prev, devices: false }));
        return deduplicatedDevices;
      } catch (err) {
        console.error('Error fetching devices:', err);
        setError(prev => ({ ...prev, devices: err.message || 'Failed to fetch devices' }));

        setLoading(prev => ({ ...prev, devices: false }));
        return [];
      }
    },
    [getTenantId, getCache, updateCache]
  );

  // Clear cache and reset circuit breaker when tenant changes
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Tenant changed, clearing event system cache and resetting circuit breaker');
    }
    clearCache();
    setLastApiCalls({
      flows: null,
      actions: null,
      devices: null,
    });
    // Only re-run when tenant id actually changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant?.id]);

  return {
    fetchFlows,
    fetchActions,
    fetchDevices,
    loading,
    error,
    tenantId: getTenantId(),
    clearCache,
  };
};

export default useEventSystemFixed;
